import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { formatApiError } from "../../lib/api";
import {
  applyFirmwareImport,
  previewFirmwareImport,
} from "../../lib/firmwareApi";

function firmwareImportError(error) {
  const detail = error?.response?.data?.detail;
  return detail?.message || formatApiError(error);
}

function IssueList({ title, items, duplicate = false }) {
  if (!items.length) return null;
  const visible = items.slice(0, 50);
  return (
    <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
      <h3 className="text-sm font-semibold text-destructive">
        {title} ({items.length})
      </h3>
      <ul className="mt-2 max-h-40 space-y-1 overflow-auto text-xs text-muted-foreground">
        {visible.map((item, index) => (
          <li key={`${duplicate ? item.duplicate_row : item.row}-${index}`}>
            {duplicate
              ? `Rows ${item.first_row} and ${item.duplicate_row}: ${item.model} / ${item.firmware_version}`
              : `Row ${item.row}: ${(item.errors || [])
                  .map((issue) => `${issue.field}: ${issue.message}`)
                  .join("; ")}`}
          </li>
        ))}
      </ul>
      {items.length > visible.length ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {items.length - visible.length} additional issues are not shown.
        </p>
      ) : null}
    </section>
  );
}

export default function FirmwareImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [operation, setOperation] = useState("");
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setError("");
    setOperation("");
    inFlight.current = false;
  }, [open]);

  if (!open) return null;

  function close() {
    if (inFlight.current) return;
    onClose();
  }

  function selectFile(event) {
    const selected = event.target.files?.[0] || null;
    setPreview(null);
    if (selected && !selected.name.toLowerCase().endsWith(".xlsx")) {
      setFile(selected);
      setError("Select an XLSX firmware workbook.");
      return;
    }
    setFile(selected);
    setError("");
  }

  async function submitPreview() {
    if (!file || inFlight.current) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setError("Select an XLSX firmware workbook.");
      return;
    }
    inFlight.current = true;
    setOperation("preview");
    setError("");
    try {
      setPreview(await previewFirmwareImport(file));
    } catch (requestError) {
      setError(firmwareImportError(requestError));
    } finally {
      inFlight.current = false;
      setOperation("");
    }
  }

  async function applyImport() {
    if (!file || !preview?.can_apply || inFlight.current) return;
    inFlight.current = true;
    setOperation("apply");
    setError("");
    try {
      const response = await applyFirmwareImport(file);
      const report = response?.data || {};
      toast.success(
        response?.message ||
          `Firmware import completed: ${report.create || 0} created, ${
            report.update || 0
          } updated, ${report.unchanged || 0} unchanged.`
      );
      onImported?.(report);
      onClose();
    } catch (requestError) {
      setError(firmwareImportError(requestError));
    } finally {
      inFlight.current = false;
      setOperation("");
    }
  }

  const invalidRows = preview?.invalid_rows || [];
  const duplicates = preview?.duplicates || [];
  const busy = Boolean(operation);

  return (
    <Modal
      title="Import Firmware"
      onClose={close}
      busy={busy}
      size="lg"
      testid="firmware-import-dialog"
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-4">
          <FileSpreadsheet className="mt-0.5 text-primary" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">Firmware XLSX workbook</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Preview validates every row before the Admin-only upsert is enabled.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-xs font-medium">XLSX file</span>
          <input
            type="file"
            accept=".xlsx"
            onChange={selectFile}
            disabled={busy}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-[var(--action-radius-normal)] file:border-0 file:bg-primary file:px-4 file:py-2 file:text-xs file:font-semibold file:text-primary-foreground"
            data-testid="firmware-import-file"
          />
          {file ? (
            <p className="mt-2 break-all text-xs text-muted-foreground" data-testid="firmware-import-filename">
              Selected: {file.name}
            </p>
          ) : null}
        </label>

        {error ? (
          <p className="text-sm text-destructive" role="alert" data-testid="firmware-import-error">
            {error}
          </p>
        ) : null}

        {preview ? (
          <div className="space-y-4" data-testid="firmware-import-preview">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Create", preview.create],
                ["Update", preview.update],
                ["Unchanged", preview.unchanged],
                ["Total", preview.total_rows],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border bg-muted/30 p-3">
                  <p className="text-xs font-medium text-muted-foreground">{label}</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums">{value || 0}</p>
                </div>
              ))}
            </div>
            {!preview.can_apply ? (
              <div className="flex items-start gap-2 text-sm text-destructive" role="status">
                <AlertTriangle className="mt-0.5 h-4 w-4" aria-hidden="true" />
                Fix every invalid or duplicate row before importing.
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-emerald-500" role="status">
                <CheckCircle2 className="mt-0.5 h-4 w-4" aria-hidden="true" />
                The workbook is valid and ready to import.
              </div>
            )}
            <IssueList title="Invalid rows" items={invalidRows} />
            <IssueList title="Duplicate rows" items={duplicates} duplicate />
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
          <Button type="button" variant="cancel" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="review"
            onClick={submitPreview}
            disabled={!file || operation === "apply"}
            loading={operation === "preview"}
            loadingLabel="Previewing…"
            data-action-category="review-preview"
            data-testid="firmware-import-preview-submit"
          >
            Preview
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={applyImport}
            disabled={!preview?.can_apply || operation === "preview"}
            loading={operation === "apply"}
            loadingLabel="Importing…"
            data-action-category="submit-confirm"
            data-testid="firmware-import-apply"
          >
            Apply Import
          </Button>
        </div>
      </div>
    </Modal>
  );
}
